// Dashboard flow SSE — connects to /api/dashboard/flow/stream using
// native EventSource (token in query param, matching the backend's
// UserAuthOrQueryToken middleware). Same pattern as SupportFloat.tsx.
//
// Lifecycle:
//   1. connect → history replay (logs + alpha events + batch-done)
//   2. batch-done → live stream
//
// Event types:
//   event: log         → { type:"log", level:"info"|"error", message, timestamp }
//   event: alpha       → { type:"alpha", alpha:{...}, sessionId, timestamp }
//   event: batch-done  → { type:"batch-done", timestamp }  — history complete

export interface DashboardLogEvent {
  type: "log";
  level: "info" | "error";
  message: string;
  timestamp: number;
}

export interface DashboardAlphaEvent {
  type: "alpha";
  alpha: {
    type: string; // "view" | "round" | "card" | "stage" | "risk_stance" | ...
    sessionId: number;
    stage?: string;
    payload?: unknown;
  };
  sessionId: number;
  timestamp: number;
}

export interface DashboardBatchDoneEvent {
  type: "batch-done";
  timestamp: number;
}

export type DashboardEvent =
  | DashboardLogEvent
  | DashboardAlphaEvent
  | DashboardBatchDoneEvent;

function getToken(): string {
  return (
    localStorage.getItem("alpha_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

export interface DashboardFlowCallbacks {
  onEvent: (e: DashboardEvent) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onAccessDenied?: () => void;
}

/**
 * Opens an SSE connection to the dashboard flow stream.
 * Returns the EventSource instance (caller must .close() on unmount).
 *
 * The stream sends history first (logs + alpha events), then a
 * "batch-done" sentinel, then live events.
 */
export function connectDashboardFlow(
  callbacks: DashboardFlowCallbacks
): EventSource {
  const token = getToken();
  const es = new EventSource(
    `/api/dashboard/flow/stream?token=${encodeURIComponent(token)}`
  );

  es.addEventListener("open", () => callbacks.onConnected?.());

  es.addEventListener("log", (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);
      callbacks.onEvent({ type: "log", ...data });
    } catch {
      // malformed event — skip
    }
  });

  es.addEventListener("alpha", (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);
      callbacks.onEvent({ type: "alpha", ...data });
    } catch {
      // malformed event — skip
    }
  });

  es.addEventListener("batch-done", (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);
      callbacks.onEvent({ type: "batch-done", ...data });
    } catch {
      // malformed — just signal done anyway
      callbacks.onEvent({ type: "batch-done", timestamp: Date.now() });
    }
  });

  es.onerror = () => {
    // Check for 403 via a separate fetch (EventSource doesn't expose status)
    fetch(
      `/api/dashboard/flow/stream?token=${encodeURIComponent(token)}`,
      { headers: { Accept: "text/event-stream" } }
    )
      .then((r) => {
        if (r.status === 403) callbacks.onAccessDenied?.();
      })
      .catch(() => {});
    callbacks.onDisconnected?.();
  };

  return es;
}
